import React, { useState, useEffect, useRef, useCallback, useImperativeHandle, forwardRef } from 'react';
import './VoiceAssistant.css';

// Cloud Function URL - update this after deploying
const VOICE_COMMAND_URL = 'https://us-central1-muso-ninjas-80a45.cloudfunctions.net/processVoiceCommand';

const VoiceAssistant = forwardRef(({ 
  userName,
  availableArtists = [],
  onCreatePlaylist,
  onPlay,
  onPause,
  onNext,
  onPrevious,
  onShuffle,
  onSavePlaylist,
  onSearch,
  isPlaying,
  renderInlineButton = false,
  buttonClassName = ''
}, ref) => {
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [response, setResponse] = useState('');
  const [showAssistant, setShowAssistant] = useState(false);
  const recognitionRef = useRef(null);
  const synthRef = useRef(window.speechSynthesis);

  // Initialize speech recognition
  useEffect(() => {
    if ('webkitSpeechRecognition' in window || 'SpeechRecognition' in window) {
      const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
      const recognition = new SpeechRecognition();
      
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.lang = 'en-US';

      recognition.onresult = (event) => {
        const current = event.resultIndex;
        const result = event.results[current];
        const transcriptText = result[0].transcript;
        
        setTranscript(transcriptText);
        
        // If final result, process the command
        if (result.isFinal) {
          processCommand(transcriptText);
        }
      };

      recognition.onerror = (event) => {
        console.error('Speech recognition error:', event.error);
        setIsListening(false);
        if (event.error === 'no-speech') {
          setResponse("I didn't hear anything. Tap to try again.");
        }
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current = recognition;
    }

    return () => {
      if (recognitionRef.current) {
        recognitionRef.current.stop();
      }
    };
  }, []);

  // Text-to-speech response
  const speak = useCallback((text) => {
    if (synthRef.current) {
      // Cancel any ongoing speech
      synthRef.current.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 1.0;
      utterance.pitch = 1.0;
      utterance.volume = 1.0;
      
      // Try to find a good voice
      const voices = synthRef.current.getVoices();
      const preferredVoice = voices.find(v => 
        v.name.includes('Google') || v.name.includes('Samantha') || v.name.includes('Alex')
      );
      if (preferredVoice) {
        utterance.voice = preferredVoice;
      }
      
      synthRef.current.speak(utterance);
    }
  }, []);

  // Start listening
  const startListening = useCallback(() => {
    if (!recognitionRef.current) {
      setResponse("Voice recognition not supported in this browser.");
      return;
    }

    setTranscript('');
    setResponse('');
    setIsListening(true);
    setShowAssistant(true);
    
    // Greet user
    const greeting = `Hey ${userName || 'there'}! What would you like?`;
    setResponse(greeting);
    speak(greeting);
    
    // Start listening after greeting
    setTimeout(() => {
      try {
        recognitionRef.current.start();
      } catch (e) {
        console.error('Error starting recognition:', e);
      }
    }, 1500);
  }, [userName, speak]);

  // Expose startListening via ref
  useImperativeHandle(ref, () => ({
    startListening
  }), [startListening]);

  // Process the voice command
  const processCommand = async (command) => {
    setIsProcessing(true);
    setResponse('Processing...');

    try {
      const res = await fetch(VOICE_COMMAND_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          command,
          userName: userName || 'friend',
          availableArtists: availableArtists.slice(0, 50) // Limit to prevent token overflow
        })
      });

      const data = await res.json();
      
      if (data.success && data.command) {
        const { action, response: jookResponse, params } = data.command;
        
        setResponse(jookResponse);
        speak(jookResponse);
        
        // Execute the action
        executeAction(action, params);
      } else {
        const errorMsg = "Sorry, I had trouble understanding that.";
        setResponse(errorMsg);
        speak(errorMsg);
      }
    } catch (error) {
      console.error('Voice command error:', error);
      const errorMsg = "Sorry, I couldn't connect. Please try again.";
      setResponse(errorMsg);
      speak(errorMsg);
    } finally {
      setIsProcessing(false);
    }
  };

  // Execute the parsed action
  const executeAction = (action, params) => {
    console.log('🎙️ Executing action:', action, params);
    
    switch (action) {
      case 'CREATE_PLAYLIST':
        if (onCreatePlaylist && params.artists) {
          onCreatePlaylist({
            artists: params.artists,
            genres: params.genres || [],
            name: params.name,
            autoPlay: params.autoPlay !== false,
            autoSave: params.autoSave || false
          });
        }
        break;
        
      case 'PLAY':
        if (onPlay) onPlay(params);
        break;
        
      case 'PAUSE':
        if (onPause) onPause();
        break;
        
      case 'SKIP':
      case 'NEXT':
        if (onNext) onNext();
        break;
        
      case 'PREVIOUS':
        if (onPrevious) onPrevious();
        break;
        
      case 'SHUFFLE':
        if (onShuffle) onShuffle();
        break;
        
      case 'SAVE_PLAYLIST':
        if (onSavePlaylist && params.name) {
          onSavePlaylist(params.name);
        }
        break;
        
      case 'SEARCH':
        if (onSearch && params.query) {
          onSearch(params.query);
        }
        break;
        
      case 'UNKNOWN':
      default:
        // Response already handled
        break;
    }
  };

  // Close assistant
  const closeAssistant = () => {
    setShowAssistant(false);
    setTranscript('');
    setResponse('');
    if (recognitionRef.current) {
      recognitionRef.current.stop();
    }
    if (synthRef.current) {
      synthRef.current.cancel();
    }
  };

  return (
    <>
      {/* Voice Button - Inline or Floating */}
      {renderInlineButton ? null : (
        <button 
          className={`voice-assistant-fab ${isListening ? 'listening' : ''}`}
          onClick={startListening}
          title="Hey Jook - Voice Commands"
        >
          🎙️
        </button>
      )}

      {/* Assistant Overlay */}
      {showAssistant && (
        <div className="voice-assistant-overlay">
          <div className="voice-assistant-modal">
            <button className="voice-close-btn" onClick={closeAssistant}>✕</button>
            
            <div className="voice-assistant-icon">
              {isListening ? (
                <div className="listening-animation">🎙️</div>
              ) : isProcessing ? (
                <div className="processing-animation">🤔</div>
              ) : (
                <div className="idle-icon">🎵</div>
              )}
            </div>

            <h2 className="voice-assistant-title">
              {isListening ? 'Listening...' : isProcessing ? 'Thinking...' : 'Hey Jook'}
            </h2>

            {transcript && (
              <div className="voice-transcript">
                <span className="transcript-label">You said:</span>
                <p>"{transcript}"</p>
              </div>
            )}

            {response && (
              <div className="voice-response">
                <p>{response}</p>
              </div>
            )}

            {!isListening && !isProcessing && (
              <button className="voice-listen-btn" onClick={startListening}>
                🎙️ Tap to speak
              </button>
            )}

            <div className="voice-examples">
              <p>Try saying:</p>
              <ul>
                <li>"Make a playlist of Elvis and Beatles"</li>
                <li>"Skip this song"</li>
                <li>"Save this as Pier Walk 1"</li>
                <li>"Shuffle my music"</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

VoiceAssistant.displayName = 'VoiceAssistant';

export default VoiceAssistant;

